if (!customElements.get("advanced-media-slideshow-panel")) {
  class AdvancedMediaSlideshowPanel extends SlideshowComponent {
    constructor() {
      super();
      this.slideVideos = Array.from(this.querySelectorAll(".slideshow__video"));
      this.primeVideos();
      if (this.slider)
        this.slider.addEventListener(
          "scroll",
          this.syncVideoPlayback.bind(this),
        );
    }

    // Start every video buffering/playing immediately on load so switching
    // tabs later is an instant show/hide instead of a fresh play() call.
    primeVideos() {
      this.slideVideos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        if (video.preload !== "auto") video.preload = "auto";
        if (video.readyState === 0) video.load();
        this.attemptPlay(video);
      });
    }

    attemptPlay(video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay was blocked (e.g. no user interaction yet); retry on
          // the first interaction so playback is already running by the
          // time the user reaches for the tab.
          const resume = () => {
            video.play().catch(() => {});
          };
          ["pointerdown", "touchstart", "keydown"].forEach((type) =>
            window.addEventListener(type, resume, {
              once: true,
              passive: true,
            }),
          );
        });
      }
    }

    syncVideoPlayback() {
      if (!this.slideVideos.length) return;
      const activeSlide = this.sliderItemsToShow
        ? this.sliderItemsToShow[this.currentPage - 1]
        : null;

      this.slideVideos.forEach((video) => {
        const isActiveSlide = activeSlide && activeSlide.contains(video);
        // Keep every video buffered/playing in the background regardless of
        // which tab is showing — switching tabs never has to wait on play().
        if (isActiveSlide) {
          if (video.paused) this.attemptPlay(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    play() {
      super.play();
      this.syncVideoPlayback();
    }

    pause() {
      super.pause();
    }
  }

  customElements.define(
    "advanced-media-slideshow-panel",
    AdvancedMediaSlideshowPanel,
  );
}

if (!customElements.get("advanced-media-slideshow-component")) {
  class AdvancedMediaSlideshowComponent extends HTMLElement {
    constructor() {
      super();
      this.tabs = Array.from(this.querySelectorAll("[data-media-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-media-panel]"));
      this.nav = this.querySelector("[data-media-nav]");
      this.indicator = this.nav
        ? this.nav.querySelector(".media-nav__indicator")
        : null;

      if (!this.panels.length) return;

      const activePanel = this.panels.find((panel) =>
        panel.classList.contains("media-tabs__panel--active"),
      );
      this.activeMediaType = activePanel
        ? activePanel.dataset.mediaPanel
        : this.panels[0].dataset.mediaPanel;

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick.bind(this));
        tab.addEventListener("keydown", this.onTabKeydown.bind(this));
      });

      if (this.indicator) {
        this.positionIndicator(false);
        window.addEventListener(
          "resize",
          this.debounce(() => this.positionIndicator(false), 150),
        );
      }
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    }

    positionIndicator(animate = true) {
      if (!this.indicator) return;
      const activeTab = this.tabs.find(
        (tab) => tab.dataset.mediaTab === this.activeMediaType,
      );
      if (!activeTab) return;

      this.indicator.style.transition = animate
        ? "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1), height 300ms cubic-bezier(0.4, 0, 0.2, 1)"
        : "none";

      this.indicator.style.width = `${activeTab.offsetWidth}px`;
      this.indicator.style.height = `${activeTab.offsetHeight}px`;
      this.indicator.style.transform = `translate(${activeTab.offsetLeft}px, ${activeTab.offsetTop}px)`;
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      this.switchToTab(tab.dataset.mediaTab);
    }

    onTabKeydown(event) {
      const key = event.key;
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
      event.preventDefault();

      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let newIndex = currentIndex;

      if (key === "ArrowLeft")
        newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (key === "ArrowRight")
        newIndex = (currentIndex + 1) % this.tabs.length;
      if (key === "Home") newIndex = 0;
      if (key === "End") newIndex = this.tabs.length - 1;

      const newTab = this.tabs[newIndex];
      newTab.focus();
      this.switchToTab(newTab.dataset.mediaTab);
    }

    switchToTab(mediaType) {
      if (!mediaType || mediaType === this.activeMediaType) return;
      this.activeMediaType = mediaType;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.mediaTab === mediaType;
        tab.classList.toggle("media-nav__item--active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.positionIndicator(true);

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.mediaPanel === mediaType;
        panel.classList.toggle("media-tabs__panel--active", isActive);

        // Videos keep playing muted in the background while hidden (see
        // AdvancedMediaSlideshowPanel), so switching tabs is a pure
        // show/hide — no play()/pause() round trip, no delay.
        if (isActive) {
          panel.removeAttribute("aria-hidden");
        } else {
          panel.setAttribute("aria-hidden", "true");
        }
      });
    }
  }

  customElements.define(
    "advanced-media-slideshow-component",
    AdvancedMediaSlideshowComponent,
  );
}
